import {
  DocumentType,
  LineType,
  NetUnitPriceStatus,
  type CustomerArticle,
  type Document,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { formatDateIso } from './format';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Escape a field value for semicolon-delimited CSV (German Excel standard).
 */
function escapeField(value: unknown): string {
  const str = String(value ?? '');
  if (str.includes('"') || str.includes(';') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Format a number with comma as decimal separator (German convention).
 */
function deNumber(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/**
 * Capitalise a document type string: "RECHNUNG" -> "Rechnung"
 */
function capitalise(type: string): string {
  return type.charAt(0) + type.slice(1).toLowerCase();
}

/**
 * Trigger a browser download for the given CSV content.
 */
function triggerDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// ---------------------------------------------------------------------------
// Document-level CSV
// ---------------------------------------------------------------------------

export function generateCsvContent(documents: Document[]): string {
  const headers = [
    'Belegnummer',
    'Typ',
    'Datum',
    'Kommission',
    'Referenz',
    'Netto Betrag',
    'Brutto Betrag',
    'Ansprechpartner',
    'Standort',
  ];

  const rows = documents.map((doc) => {
    const isLieferschein = doc.type === DocumentType.LIEFERSCHEIN;
    return [
      doc.documentNumber,
      capitalise(doc.type),
      formatDateIso(doc.documentDate),
      doc.commission ?? '',
      doc.yourReference ?? '',
      isLieferschein ? '' : deNumber(doc.netTotal ?? 0),
      isLieferschein ? '' : deNumber(doc.grossTotal ?? 0),
      doc.contact ?? '',
      doc.locationCode ?? '',
    ]
      .map(escapeField)
      .join(';');
  });

  // BOM + CRLF for Excel UTF-8 compatibility
  return '\uFEFF' + [headers.join(';'), ...rows].join('\r\n');
}

export function exportToCsv(
  documents: Document[],
  filename: string = 'export.csv'
): void {
  triggerDownload(generateCsvContent(documents), filename);
}

// ---------------------------------------------------------------------------
// Line-item-level CSV
// ---------------------------------------------------------------------------

export function generateLineItemsCsvContent(documents: Document[]): string {
  const headers = [
    'Belegnummer',
    'Typ',
    'Datum',
    'Kommission',
    'Referenz',
    'Positionsnummer',
    'Artikelnummer',
    'Beschreibung',
    'Menge',
    'Einheit',
    'Pakete',
    'Menge/VE',
    'Einzelpreis',
    'NettoPE',
    'Gesamtpreis',
    'Ansprechpartner',
    'Standort',
  ];

  const rows: string[] = [];

  documents.forEach((doc) => {
    const isLieferschein = doc.type === DocumentType.LIEFERSCHEIN;
    const nettoPeApplicable =
      doc.netUnitPriceStatus !== NetUnitPriceStatus.NOT_APPLICABLE;
    doc.lines
      .filter((line) => line.lineType === LineType.ARTICLE)
      .forEach((line) => {
        const nettoPe =
          !isLieferschein && nettoPeApplicable && line.netUnitPrice != null
            ? deNumber(line.netUnitPrice)
            : '';
        const row = [
          doc.documentNumber,
          capitalise(doc.type),
          formatDateIso(doc.documentDate),
          doc.commission ?? '',
          doc.yourReference ?? '',
          line.lineNumber,
          line.articleNumber ?? '',
          line.description ?? '',
          String(line.quantity ?? 0).replace('.', ','),
          line.unit ?? '',
          line.packageQuantity != null
            ? String(line.packageQuantity).replace('.', ',')
            : '',
          line.quantityPerPackage != null
            ? String(line.quantityPerPackage).replace('.', ',')
            : '',
          isLieferschein ? '' : deNumber(line.unitPrice ?? 0),
          nettoPe,
          isLieferschein ? '' : deNumber(line.amount ?? 0),
          doc.contact ?? '',
          doc.locationCode ?? '',
        ]
          .map(escapeField)
          .join(';');

        rows.push(row);
      });
  });

  return '\uFEFF' + [headers.join(';'), ...rows].join('\r\n');
}

export function exportLineItemsToCsv(
  documents: Document[],
  filename: string = 'export_positionen.csv'
): void {
  triggerDownload(generateLineItemsCsvContent(documents), filename);
}

// ---------------------------------------------------------------------------
// Article-level CSV (CRM Artikelsuche)
// ---------------------------------------------------------------------------

export function generateArticleCsvContent(articles: CustomerArticle[]): string {
  const headers = [
    'Artikelnummer',
    'Bezeichnung',
    'Bezeichnung 2',
    'Einheit',
    'Letzter Netto-Preis',
    'Gesamtmenge',
    'Anzahl Belege',
    'Letztes Rechnungsdatum',
    'Letzter Belegtyp',
    'Letzte Rechnungsnummer',
  ];

  const rows = articles.map((article) => {
    // `last*` fields are null when the article has never appeared on an invoice
    // (VRG) for this customer — emit empty cells instead of "0,00" / "Invalid Date".
    return [
      article.articleNumber,
      article.description ?? '',
      article.description2 ?? '',
      article.unit ?? '',
      article.lastNetUnitPrice != null ? deNumber(article.lastNetUnitPrice) : '',
      deNumber(article.totalQuantity),
      String(article.documentCount),
      article.lastPurchaseDate ? formatDateIso(article.lastPurchaseDate) : '',
      article.lastDocumentType ? capitalise(article.lastDocumentType) : '',
      article.lastDocumentNumber ?? '',
    ]
      .map(escapeField)
      .join(';');
  });

  // BOM + CRLF for Excel UTF-8 compatibility
  return '\uFEFF' + [headers.join(';'), ...rows].join('\r\n');
}

export function exportArticlesToCsv(
  articles: CustomerArticle[],
  filename: string = 'artikelauswahl.csv'
): void {
  triggerDownload(generateArticleCsvContent(articles), filename);
}
